import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { useAuthStore } from '@/lib/auth-store'
import { Logo } from '@/components/Logo'
import type { SessionUser } from '@/lib/session-types'

interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: SessionUser
}

export function LoginPage() {
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const data = await apiFetch<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
        skipAuth: true,
      })
      setSession(data)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError('Unexpected error. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-b from-ovation-paper to-white px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-xl border border-ovation-ink/10 bg-white p-7 shadow-xl shadow-ovation-ink/5"
        >
          <div>
            <h1 className="font-display text-2xl tracking-tight">Sign in</h1>
            <p className="mt-1 text-sm text-ovation-muted">
              Access the Ovation back office.
            </p>
          </div>

          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-ovation-muted hover:text-ovation-ink"
              >
                Forgot?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 pr-10 text-sm focus:border-ovation-ink focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ovation-muted hover:text-ovation-ink"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="space-y-2 border-t border-ovation-ink/10 pt-4 text-center text-xs text-ovation-muted">
            <div>
              Crew, musician, engineer?{' '}
              <Link
                to="/join"
                className="font-medium text-ovation-ink hover:underline"
              >
                Join free
              </Link>
            </div>
            <div>
              Management or artist team?{' '}
              <Link
                to="/join/artist"
                className="font-medium text-ovation-ink hover:underline"
              >
                Sign up as an artist
              </Link>
            </div>
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-ovation-muted">
          The preparation is ours. The ovation is yours.
        </p>
        <p className="mt-2 text-center text-[9px] uppercase tracking-[0.18em] text-ovation-muted/70">
          <a
            href="https://geekybee.net"
            target="_blank"
            rel="noreferrer"
            className="hover:text-ovation-ink"
          >
            A Geekybee.net product
          </a>
        </p>
      </div>
    </div>
  )
}
